import { useState, useCallback } from 'react'
import { format } from 'date-fns'
import { Trash2, ChevronDown, ChevronUp, Download, Search, X } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DispositionBadge } from '@/features/dispositionBadge'
import { useLeads, useUpdateLeadStatus, useDeleteLead, useExportLeads } from '@/entities/lead'
import type { CrmListRow } from '@intone/shared/types'

interface DialogViewLeadsProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  list: CrmListRow | null
  dispositions?: string[]
}

// Lead data display component
function LeadData({ data }: { data: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false)
  const entries = Object.entries(data)

  if (entries.length === 0) return <span className="text-gray-400">-</span>

  const displayCount = expanded ? entries.length : 2
  const hasMore = entries.length > 2

  return (
    <div className="text-detail">
      {entries.slice(0, displayCount).map(([key, value]) => (
        <div key={key} className="truncate">
          <span className="font-medium">{key}:</span> {String(value)}
        </div>
      ))}
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-blue-600 hover:underline flex items-center gap-1 mt-1"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" /> Show {entries.length - 2} more
            </>
          )}
        </button>
      )}
    </div>
  )
}

export function DialogViewLeads({
  open,
  onOpenChange,
  list,
  dispositions = ['NEW', 'SUCCESS', 'CALLBK', 'NI', 'NQ', 'DNC', 'NA', 'AM'],
}: DialogViewLeadsProps) {
  const [page, setPage] = useState(0)
  const [disposition, setDisposition] = useState<string>('all')
  const [searchInput, setSearchInput] = useState('')
  const [activeSearch, setActiveSearch] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const pageSize = 50
  const exportLeads = useExportLeads()

  const { data, isLoading, refetch } = useLeads(
    list
      ? {
          list_id: list.id,
          page_id: page,
          page_size: pageSize,
          disposition: disposition === 'all' ? undefined : disposition,
          search: activeSearch || undefined,
        }
      : null
  )

  const updateStatus = useUpdateLeadStatus()
  const deleteLead = useDeleteLead()

  const handleSearch = useCallback(() => {
    setActiveSearch(searchInput.trim())
    setPage(0)
  }, [searchInput])

  const clearSearch = useCallback(() => {
    setSearchInput('')
    setActiveSearch('')
    setPage(0)
  }, [])

  const handleExportCSV = useCallback(async () => {
    if (!list) return
    setIsExporting(true)
    try {
      const leads = await exportLeads(list.id)
      if (leads.length === 0) {
        toast.info('No leads to export')
        return
      }

      const dataKeys = new Set<string>()
      for (const lead of leads) {
        for (const key of Object.keys(lead.data)) dataKeys.add(key)
      }
      const sortedKeys = Array.from(dataKeys).sort()

      const headers = ['Phone', 'Status', 'Last Call', ...sortedKeys]
      const csvRows = [headers.join(',')]

      const escapeCsv = (val: string) =>
        val.includes(',') || val.includes('"') || val.includes('\n')
          ? `"${val.replace(/"/g, '""')}"`
          : val

      for (const lead of leads) {
        const row = [
          escapeCsv(lead.phone_number),
          escapeCsv(lead.status),
          lead.last_call_ts ? format(new Date(lead.last_call_ts), 'yyyy-MM-dd HH:mm') : '',
          ...sortedKeys.map((k) => escapeCsv(String(lead.data[k] ?? ''))),
        ]
        csvRows.push(row.join(','))
      }

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${list.name || 'leads'}.csv`
      link.click()
      URL.revokeObjectURL(url)
      toast.success(`Exported ${leads.length} leads`)
    } catch {
      toast.error('Failed to export leads')
    } finally {
      setIsExporting(false)
    }
  }, [list, exportLeads])

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      await updateStatus.mutateAsync({ leadId, status: newStatus })
      refetch()
      toast.success('Status updated')
    } catch (error) {
      toast.error('Failed to update status')
    }
  }

  const handleDelete = async (leadId: string) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return
    try {
      await deleteLead.mutateAsync(leadId)
      refetch()
      toast.success('Lead deleted')
    } catch (error) {
      toast.error('Failed to delete lead')
    }
  }

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Leads in {list?.name || 'List'}
            {data && <span className="text-gray-500 font-normal ml-2">({data.total} total)</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 pb-4">
          <div className="w-40">
            <Select value={disposition} onValueChange={(v) => { setDisposition(v); setPage(0) }}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {dispositions.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1 flex-1 min-w-[200px]">
            <Input
              placeholder="Search phone, name, data..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="h-9"
            />
            {activeSearch ? (
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={clearSearch}>
                <X className="h-4 w-4" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={handleExportCSV}
            disabled={isExporting}
          >
            <Download className="h-3.5 w-3.5 mr-1" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Phone</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-36">Last Call</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="w-16">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : !data?.leads?.length ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No leads found
                  </TableCell>
                </TableRow>
              ) : (
                data.leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-mono">{lead.phone_number || '-'}</TableCell>
                    <TableCell>
                      <DispositionBadge
                        disposition={lead.status || 'NEW'}
                        dispositions={dispositions}
                        isEditable={true}
                        onChange={(newStatus) => handleStatusChange(lead.id, newStatus)}
                      />
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {lead.last_call_ts
                        ? lead.last_call_id
                          ? <a href={`/history?call_id=${lead.last_call_id}`} className="text-blue-600 hover:underline">{format(new Date(lead.last_call_ts), 'MMM d, HH:mm')}</a>
                          : format(new Date(lead.last_call_ts), 'MMM d, HH:mm')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <LeadData data={lead.data} />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(lead.id)}
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4">
            <span className="text-body text-gray-600">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}